import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _isEqual from 'lodash/isEqual';
import _get from 'lodash/get';

import { fetchZolaMetadata, fetchBatchZolaMetadata } from 'actions/UploadcareActions';
import { getCachedMetadata } from 'selectors/uploadcareSelectors';

export const useSingleImageMetadata = imageUUID => {
  // Redux state
  const cachedMetadata = useSelector(getCachedMetadata);
  const dispatch = useDispatch();
  const loadCache = useCallback(zolaImageId => dispatch(fetchZolaMetadata(zolaImageId)), [
    dispatch,
  ]);

  // Local state
  const [singleImageMetadata, setSingleImageMetadata] = useState(
    imageUUID && cachedMetadata[imageUUID]
  );

  useEffect(() => {
    const cachedImageMetadata = imageUUID && cachedMetadata[imageUUID];

    // If the Zola image is set but its metadata hasn't started fetching yet, fetch it.
    if (imageUUID && !cachedImageMetadata) {
      loadCache(imageUUID);
      return;
    }

    // If the metadata is empty or still fetching, stop here
    if (!cachedImageMetadata || cachedImageMetadata.loading) return;

    // To avoid useless rerenders, only update image metadata when it actually changes.
    if (!_isEqual(cachedImageMetadata, singleImageMetadata)) {
      setSingleImageMetadata(cachedImageMetadata);
    }
  }, [cachedMetadata, imageUUID, loadCache, singleImageMetadata]);

  return singleImageMetadata;
};

const extractBatchMetadata = (imageUUIDs, cachedMetadata) => {
  if (!imageUUIDs || !cachedMetadata) return null;

  // Until all requested metadata is available, return null
  const isAllMetadataAvailable = imageUUIDs.every(
    id => _get(cachedMetadata, [id, 'loading']) === false
  );
  if (!isAllMetadataAvailable) return null;

  // Once all metadata is loaded, extract and return the requested data from the full cache
  const batchMetadata = {};
  imageUUIDs.forEach(id => {
    batchMetadata[id] = cachedMetadata[id] || null;
  });
  return batchMetadata;
};

export const useBatchImageMetadata = imageUUIDs => {
  // Redux state
  const cachedMetadata = useSelector(getCachedMetadata);
  const dispatch = useDispatch();
  const loadCache = useCallback(zolaImageIds => dispatch(fetchBatchZolaMetadata(zolaImageIds)), [
    dispatch,
  ]);

  // Local state
  const [batchImageMetadata, setBatchImageMetadata] = useState(
    extractBatchMetadata(imageUUIDs, cachedMetadata)
  );

  useEffect(() => {
    const isBatchInitialized = !!imageUUIDs && imageUUIDs.every(uuid => !!cachedMetadata[uuid]);

    // If the Zola image IDs are set but metadata hasn't started fetching yet, fetch it.
    if (imageUUIDs && !isBatchInitialized) {
      loadCache(imageUUIDs);
      return;
    }
    // If the metadata is unavailable, stop here
    if (!isBatchInitialized) return;

    const cachedBatchMetadata = extractBatchMetadata(imageUUIDs, cachedMetadata);

    // To avoid useless rerenders, only update batch image metadata when it actually changes.
    if (!_isEqual(cachedBatchMetadata, batchImageMetadata)) {
      setBatchImageMetadata(cachedBatchMetadata);
    }
  }, [cachedMetadata, imageUUIDs, loadCache, batchImageMetadata]);

  return batchImageMetadata;
};
